/**
 * Format registry (spec §8). Ordered detectors, first match wins; a config
 * `format` beats all of them. Pure — no React, no clock.
 */
import { isPlainObject } from "../normalizeExtract";
import { displayCase, keyWords, sentenceCase } from "./labels";
import { displayDate, displayDateTime, isoDate, parseDateLike } from "./dates";

// ── empties (spec §3.3) ──────────────────────────────────────────────────────
export const isEmptyValue = (v) =>
  v === null ||
  v === undefined ||
  (typeof v === "string" && !v.trim()) ||
  (Array.isArray(v) && v.length === 0) ||
  (isPlainObject(v) && Object.keys(v).length === 0);

/** Review-layer keys whose null means "not judged" — hidden, never "No". */
export const REVIEW_KEYS = new Set([
  "image_quality_rating", "photo_present", "photo_obscured", "legible",
  "requires_re_upload", "re_upload_reason", "is_us_document", "scan_quality",
  "expected_note_count_met",
]);

// ── detectors ────────────────────────────────────────────────────────────────
const SENSITIVE_WORDS = /^(ssn|socialsecurity|socialsecuritynumber|tin|itin|taxid|taxidnumber|accountnumber|acctnumber|routingnumber|aba|cardnumber|passportnumber)$/;

export const isSensitiveKey = (key, value) => {
  const w = keyWords(key);
  for (let i = 0; i < w.length; i += 1) {
    for (let j = i + 1; j <= w.length; j += 1) {
      if (SENSITIVE_WORDS.test(w.slice(i, j).join(""))) return true;
    }
  }
  return (
    typeof value === "string" &&
    /^\d{3}-?\d{2}-?\d{4}$/.test(value.trim()) &&
    /ssn|tax|tin/i.test(String(key))
  );
};

export const maskValue = (value) => {
  const s = String(value ?? "");
  if (/^\d{3}-?\d{2}-?\d{4}$/.test(s)) return `***-**-${s.slice(-4)}`;
  return s.length > 4 ? `•••• ${s.slice(-4)}` : "••••";
};

const BOOL_STRINGS = { yes: true, y: true, true: true, no: false, n: false, false: false };
const BOOL_PREFIX = /^(is|has|can|should|was|requires|expected)([A-Z_])/;
const BOOL_SUFFIX = /(_(present|obscured|required|met)|flag)$/i;
export const isBooleanKey = (key) => BOOL_PREFIX.test(String(key)) || BOOL_SUFFIX.test(String(key));
const asBool = (key, v) => {
  if (typeof v === "boolean") return v;
  if (typeof v === "string" && isBooleanKey(key)) {
    const b = BOOL_STRINGS[v.trim().toLowerCase()];
    if (b !== undefined) return b;
  }
  return undefined;
};

const MONEY_KEY = /(cost|budget|total|amount|price|fee|fees|contingency|balance|payment|income|rent|salary|principal|escrow|taxes|insurance|arv|payoff|draw|deposit|limit|value)(usd|eur|gbp|cad)?$/i;
const MONEY_GUARD = /(number|id|type|name|count|status|date)$/i;
const MONEY_STRING = /^\(?\s*-?\s*(?:[$€£]|USD|EUR|GBP|CAD)\s*-?[\d,]+(?:\.\d+)?\s*\)?$/i;
const PCT_KEY = /(percent|pct|rate|ratio|ltv|dti|ltc|confidence|apr|yield|margin|agreementrate)$/i;
const PCT_FINE = /(rate|apr|yield|margin)$/i;
const ID_KEY = /(^id$|Id$|number$|no$|num$|^ein$|loanNumber|parcel|apn|vin|license(number)?$)/i;
const ENUM_KEY = /(status|type|kind|stage|category|class|grade|condition|level|rating|quality)$/i;
const PHONE_KEY = /phone|mobile|fax|tel(ephone)?$/i;
const NO_GROUPING = /year|zip|postal|code/i;

export const toNumber = (v) => {
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (typeof v !== "string") return null;
  const s = v.trim();
  const neg = /^\(.*\)$/.test(s) || /^-/.test(s.replace(/^[($€£\s]+|USD|EUR|GBP|CAD/gi, ""));
  const digits = s.replace(/[^\d.]/g, "");
  if (!digits || !/^\d*\.?\d+$/.test(digits)) return null;
  const n = Number(digits);
  return Number.isFinite(n) ? (neg ? -n : n) : null;
};

const NUMERIC_STRING = /^-?[\d,]+(\.\d+)?$/;

const currencyFor = (key, value, siblings) => {
  const suffix = String(key).match(/(usd|eur|gbp|cad)$/i);
  if (suffix) return suffix[1].toUpperCase();
  const sib = siblings?.currency || siblings?.currencyCode;
  if (typeof sib === "string" && /^[A-Z]{3}$/i.test(sib)) return sib.toUpperCase();
  if (typeof value === "string") {
    if (value.includes("€")) return "EUR";
    if (value.includes("£")) return "GBP";
    const code = value.match(/USD|EUR|GBP|CAD/i);
    if (code) return code[0].toUpperCase();
  }
  return "USD";
};

export const fmtMoney = (n, currency = "USD") => {
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(n);
  } catch {
    return `$${Number(n).toLocaleString("en-US")}`;
  }
};

export const fmtPercent = (n, key = "") => {
  const pct = n >= 0 && n <= 1 ? n * 100 : n;
  const digits = PCT_FINE.test(key) ? 3 : 1;
  return `${Number(pct.toFixed(digits))}%`;
};

export const fmtNumber = (n, key = "") =>
  NO_GROUPING.test(key) ? String(n) : n.toLocaleString("en-US", { maximumFractionDigits: 4 });

const fmtPhone = (s) => {
  const d = String(s).replace(/\D/g, "");
  if (d.length === 10) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  if (d.length === 11 && d[0] === "1") return `+1 (${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7)}`;
  return String(s);
};

const truncateMiddle = (s, max = 48) =>
  s.length <= max ? s : `${s.slice(0, max / 2 - 1)}…${s.slice(-(max / 2 - 2))}`;

// Tone maps for known enums (spec §8.1 #10).
const TONE_MAPS = [
  { none: "ok", low: "ok", medium: "warn", high: "crit", critical: "crit" },
  { excellent: "ok", good: "ok", fair: "warn", poor: "crit", unreadable: "crit" },
  { resolved: "ok", absent: "ok", open: "warn", unknown: "neutral" },
];
export const enumTone = (value) => {
  const v = String(value ?? "").trim().toLowerCase();
  for (const map of TONE_MAPS) if (map[v]) return map[v];
  return undefined;
};

/** Which format a value should render as. */
export const detectFormat = (key, value, siblings) => {
  const k = String(key ?? "");
  if (asBool(k, value) !== undefined) return "boolean";
  if (isPlainObject(value) && "amount" in value && Object.keys(value).length <= 3) return "money";
  if (typeof value === "number" || (typeof value === "string" && NUMERIC_STRING.test(value.trim()))) {
    if (MONEY_KEY.test(k) && !MONEY_GUARD.test(k)) return "money";
    if (PCT_KEY.test(k)) return "percent";
  }
  if (typeof value === "string") {
    const s = value.trim();
    if (MONEY_STRING.test(s)) return "money";
    const dt = parseDateLike(s);
    if (dt) return dt.kind;
    if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s)) return "email";
    if (PHONE_KEY.test(k) && s.replace(/\D/g, "").length >= 10) return "phone";
    if (/^[\d\s().+-]+$/.test(s) && /[\s().-]/.test(s) && [10, 11].includes(s.replace(/\D/g, "").length) && !ID_KEY.test(k))
      return "phone";
    if (/^https?:\/\//i.test(s)) return "url";
    if (ID_KEY.test(k) && !/\s/.test(s) && s.length <= 40) return "id";
    if (ENUM_KEY.test(k) && s.length <= 32) return "enum";
    if (NUMERIC_STRING.test(s) && !/^0\d/.test(s)) return "number";
    if (s.length >= 160) return "prose";
    return "text";
  }
  if (typeof value === "number") {
    if (ID_KEY.test(k) && Number.isInteger(value)) return "id";
    return "number";
  }
  return "json";
};

/**
 * Format one scalar.
 * @returns {{ format, display, copyValue, tone?, raw }}
 */
export const formatScalar = (key, value, { format: forced, siblings } = {}) => {
  const format = forced || detectFormat(key, value, siblings);
  const k = String(key ?? "");
  const out = (display, copyValue = display, extra = {}) => ({
    format,
    display,
    copyValue: String(copyValue ?? ""),
    ...extra,
  });
  switch (format) {
    case "boolean": {
      const b = asBool(k, value) ?? !!value;
      return out(b ? "Yes" : "No", b ? "Yes" : "No");
    }
    case "money": {
      const obj = isPlainObject(value) ? value : null;
      const n = toNumber(obj ? obj.amount : value);
      if (n === null) return out(String(value), String(value));
      const cur = obj?.currency ? String(obj.currency).toUpperCase() : currencyFor(k, value, siblings);
      return out(fmtMoney(n, cur), n);
    }
    case "percent": {
      const n = toNumber(value);
      return n === null ? out(String(value)) : out(fmtPercent(n, k), n);
    }
    case "date": {
      const d = parseDateLike(value);
      return d ? out(displayDate(d), isoDate(d), { date: d }) : out(String(value));
    }
    case "datetime": {
      const d = parseDateLike(value);
      return d ? out(displayDateTime(d.iso || value), value, { date: d }) : out(String(value));
    }
    case "phone":
      return out(fmtPhone(value), String(value));
    case "url":
      return out(truncateMiddle(String(value)), String(value));
    case "email":
    case "id":
      return out(String(value), String(value));
    case "enum":
      return out(sentenceCase(value), String(value), { tone: enumTone(value) });
    case "number": {
      const n = toNumber(value);
      return n === null ? out(String(value)) : out(fmtNumber(n, k), n);
    }
    case "prose":
      return out(String(value));
    case "json":
      return out(JSON.stringify(value));
    default:
      return out(displayCase(value), String(value));
  }
};

// ── addresses (spec §8.6) ────────────────────────────────────────────────────
const ADDRESS_KEYS = [
  "street", "streetNumber", "streetName", "line1", "address1", "city", "state",
  "zip", "zipCode", "postalCode", "county", "country",
];
export const isAddressLike = (v) =>
  isPlainObject(v) &&
  (typeof v.fullAddress === "string" ||
    ADDRESS_KEYS.filter((k) => v[k] !== undefined).length >= 2);

export const addressZip = (a) => a.zip ?? a.zipCode ?? a.postalCode ?? null;

export const addressDisplay = (a) => {
  if (typeof a.fullAddress === "string" && a.fullAddress.trim()) return a.fullAddress.trim();
  const line1 =
    a.line1 ||
    a.address1 ||
    a.street ||
    [a.streetNumber, a.streetName].filter(Boolean).join(" ");
  const unit = a.addressUnitNumber || a.line2 || a.address2;
  const tail = [a.state, addressZip(a)].filter(Boolean).join(" ");
  return [line1, unit, a.city, tail].filter((x) => x && String(x).trim()).join(", ") || null;
};

/** Which of state / ZIP is missing, as words ("state and ZIP"), or null. */
export const addressMissing = (a) => {
  const missing = [];
  if (isEmptyValue(a.state)) missing.push("state");
  if (isEmptyValue(addressZip(a))) missing.push("ZIP");
  return missing.length ? missing.join(" and ") : null;
};
